export enum ECloudinaryTransform {
  cover16x9 = "cover16x9",
  cover4x3 = "cover4x3",
  cover3x2 = "cover3x2",
  cover2x6 = "cover2x6",
  fill16x9 = "fill16x9",
  fill3x2 = "fill3x2",
  fit16x9 = "fit16x9",
  fit3x2 = "fit3x2",
  limit = "limit",
  bgcrop = "bgcrop",
  thumb3x4 = "thumb3x4",
  thumb9x10 = "thumb9x10",
  thumb1x1 = "thumb1x1",
}

export enum EWsrvTransform {
  cover16x9 = "cover16x9",
  cover4x3 = "cover4x3",
  cover3x2 = "cover3x2",
  cover2x6 = "cover2x6",
  fill16x9 = "fill16x9",
  fill3x2 = "fill3x2",
  fill1x1 = "fill1x1",
  fit16x9 = "fit16x9",
  fit4x3 = "fit4x3", 
  fit3x2 = "fit3x2",
  fit3x4 = "fit3x4",
  fit1x1 = "fit1x1",
  limit = "limit",
  bgcrop = "bgcrop",
  thumb3x4 = "thumb3x4",
  thumb9x10 = "thumb9x10",
  thumb1x1 = "thumb1x1",
}

export interface IMediaItem {
  url: string;
  thumbnailURL?: string;
  dimensions?: [width: number, height: number];
  caption?: string;
  description?: string;
  mimeType?: string;
  sources?: { srcSet: string; media: string }[];
}

export interface IOpenHouseItem {
  startDate: string;
  endDate: string;
  headLine: string;
}

export interface IMakeCloudinaryURL {
  preset: ECloudinaryTransform;
  originURL: string;
  width?: number;
  height?: number;
  background?: string;
  quality?: string;
  cloudinaryFetch?: string;
}

export interface IMakeWesrvURL {
  preset: EWsrvTransform;
  originURL: string;
  width?: number;
  height?: number;
  background?: string;
  quality?: string;
  output?: 'jpg' | 'webp' | 'avif' | 'png';
}

// ImageKit interfaces and enums
export enum EImageKitTransform {
  cover16x9 = "cover16x9",
  cover4x3 = "cover4x3",
  cover3x2 = "cover3x2",
  cover2x6 = "cover2x6",
  fill16x9 = "fill16x9",
  fill3x2 = "fill3x2",
  fill1x1 = "fill1x1",
  fit16x9 = "fit16x9",
  fit4x3 = "fit4x3", 
  fit3x2 = "fit3x2",
  fit3x4 = "fit3x4",
  fit1x1 = "fit1x1",
  limit = "limit",
  bgcrop = "bgcrop",
  thumb3x4 = "thumb3x4",
  thumb9x10 = "thumb9x10",
  thumb1x1 = "thumb1x1",
}

export interface IMakeImageKitURL {
  preset: EImageKitTransform;
  originURL: string;
  width?: number;
  height?: number;
  background?: string;
  quality?: number;
  output?: 'jpg' | 'webp' | 'avif' | 'png';
  urlEndpoint?: string; // ImageKit URL endpoint
}

export const makeCloudinaryURL = (args: IMakeCloudinaryURL) => {
  let transform = ["f_auto"];
  args.quality ? transform.push("q_" + args.quality) : transform.push("q_auto");
  args.background && transform.push("b_" + args.background);
  if (args.preset != ECloudinaryTransform.bgcrop) {
    args.width && transform.push("w_" + args.width);
  }
  // args.height && transform.push("h_" + args.height);
  switch (args.preset) {
    case ECloudinaryTransform.cover16x9:
      transform.push("c_fill");
      args.width && transform.push("h_" + Math.round((args.width * 9) / 16));
      break;
    case ECloudinaryTransform.cover4x3:
      transform.push("c_fill");
      args.width && transform.push("h_" + Math.round((args.width * 3) / 4));
      break;
    case ECloudinaryTransform.cover3x2:
      transform.push("c_fill");
      args.width && transform.push("h_" + Math.round((args.width * 2) / 3));
      break;
    case ECloudinaryTransform.cover2x6:
      transform.push("c_fill");
      args.width && transform.push("h_" + Math.round((args.width * 6) / 2));
      break;
    case ECloudinaryTransform.fill16x9:
      transform.push("c_lpad");
      args.width && transform.push("h_" + Math.round((args.width * 9) / 16));
      break;
    case ECloudinaryTransform.fill3x2:
      transform.push("c_lpad");
      args.width && transform.push("h_" + Math.round((args.width * 2) / 3));
      break;
    case ECloudinaryTransform.fit16x9:
      transform.push("c_fit");
      args.width && transform.push("h_" + Math.round((args.width * 9) / 16));
      break;
    case ECloudinaryTransform.fit3x2:
      transform.push("c_fit");
      args.width && transform.push("h_" + Math.round((args.width * 2) / 3));
      break;
    case ECloudinaryTransform.limit:
      transform.push("c_limit");
      break;
    case ECloudinaryTransform.bgcrop:
      transform.push("c_crop");
      transform.push("g_center");
      transform.push("ar_0.5");
      break;
    case ECloudinaryTransform.thumb3x4:
      transform.push("c_fill");
      transform.push("g_face");
      args.width && transform.push("h_" + Math.round((args.width * 4) / 3));
      break;
    case ECloudinaryTransform.thumb9x10:
      transform.push("c_fill");
      transform.push("g_face");
      args.width && transform.push("h_" + Math.round((args.width * 10) / 9));
      break;
    case ECloudinaryTransform.thumb1x1:
      transform.push("c_fill");
      transform.push("g_face");
      args.width && transform.push("h_" + args.width);
      break;
  }
  return (
    (args.cloudinaryFetch ||
      "https://res.cloudinary.com/dnsr06bev/image/fetch/") +
    transform.join(",") +
    "/" +
    encodeURI(args.originURL)
  );
};

export const makeWsrvURL = (args: IMakeWesrvURL) => {
  let transform = [`output=${args.output || 'webp'}`, `bg=${args.background || 'white'}`];
  args.quality ? transform.push("q=" + args.quality) : transform.push("q=70");
  args.width && transform.push("w=" + args.width);
  switch (args.preset) {
    case EWsrvTransform.cover16x9:
      transform.push("fit=cover");
      args.width && transform.push("h=" + Math.round((args.width * 9) / 16));
      break;
    case EWsrvTransform.cover4x3:
      transform.push("fit=cover");
      args.width && transform.push("h=" + Math.round((args.width * 3) / 4));
      break;
    case EWsrvTransform.cover3x2:
      transform.push("fit=cover");
      args.width && transform.push("h=" + Math.round((args.width * 2) / 3));
      break;
    case EWsrvTransform.cover2x6:
      transform.push("fit=cover");
      args.width && transform.push("h=" + Math.round((args.width * 6) / 2));
      break;
    case EWsrvTransform.fill16x9:
      transform.push("fit=contain");
      args.width && transform.push("h=" + Math.round((args.width * 9) / 16));
      break;
    case EWsrvTransform.fill3x2:
      transform.push("fit=contain");
      args.width && transform.push("h=" + Math.round((args.width * 2) / 3));
      break;
    case EWsrvTransform.fill1x1:
      transform.push("fit=contain");
      args.width && transform.push("h=" + Math.round((args.width * 1) / 1));
      break;
    case EWsrvTransform.fit16x9:
      transform.push("fit=inside");
      args.width && transform.push("h=" + Math.round((args.width * 9) / 16));
      break;
    case EWsrvTransform.fit4x3:
      transform.push("fit=inside");
      args.width && transform.push("h=" + Math.round((args.width * 3) / 4));
      break;
    case EWsrvTransform.fit3x2:
      transform.push("fit=inside");
      args.width && transform.push("h=" + Math.round((args.width * 2) / 3));
      break;
    case EWsrvTransform.fit3x4:
      transform.push("fit=inside");
      args.width && transform.push("h=" + Math.round((args.width * 4) / 3));
      break;
    case EWsrvTransform.fit1x1:
      transform.push("fit=inside");
      args.width && transform.push("h=" + Math.round((args.width * 1) / 1));
      break;
    case EWsrvTransform.limit:
      transform.push("fit=inside");
      break;
    case EWsrvTransform.bgcrop:
      transform.push("fit=cover");
      transform.push("a=center");
      args.width && transform.push("h=" + Math.round((args.width / 2)));
      break;
    case EWsrvTransform.thumb3x4:
      transform.push("fit=cover");
      transform.push("a=center");
      args.width && transform.push("h=" + Math.round((args.width * 4) / 3));
      break;
    case EWsrvTransform.thumb9x10:
      transform.push("fit=cover");
      transform.push("a=center");
      args.width && transform.push("h=" + Math.round((args.width * 10) / 9));
      break;
    case EWsrvTransform.thumb1x1:
      transform.push("fit=cover");
      transform.push("a=top-left");
      args.width && transform.push("h=" + args.width);
      break;
  }

  return `//wsrv.nl/?url=${encodeURI(args.originURL)}&${transform.join("&")}`
};

// ImageKit URL generation function
export const makeImageKitURL = (args: IMakeImageKitURL) => {
  const {
    preset,
    originURL,
    width,
    height,
    background = 'FFFFFF',
    quality = 80,
    output = 'webp',
    urlEndpoint = 'https://ik.imagekit.io/ggmtouwe2' // Your ImageKit endpoint
  } = args;

  // Build transformation parameters array
  const transformations: string[] = [];

  // Add format first for better optimization
  transformations.push(`f-${output}`);
  
  // Add quality
  transformations.push(`q-${quality}`);

  // Add width if specified
  if (width) {
    transformations.push(`w-${width}`);
  }

  // Handle different preset transformations
  switch (preset) {
    case EImageKitTransform.cover16x9:
      transformations.push('c-maintain_ratio');
      if (width) {
        const calculatedHeight = Math.round((width * 9) / 16);
        transformations.push(`h-${calculatedHeight}`);
      }
      break;
      
    case EImageKitTransform.cover4x3:
      transformations.push('c-maintain_ratio');
      if (width) {
        const calculatedHeight = Math.round((width * 3) / 4);
        transformations.push(`h-${calculatedHeight}`);
      }
      break;
      
    case EImageKitTransform.cover3x2:
      transformations.push('c-maintain_ratio');
      if (width) {
        const calculatedHeight = Math.round((width * 2) / 3);
        transformations.push(`h-${calculatedHeight}`);
      }
      break;
      
    case EImageKitTransform.cover2x6:
      transformations.push('c-maintain_ratio');
      if (width) {
        const calculatedHeight = Math.round((width * 6) / 2);
        transformations.push(`h-${calculatedHeight}`);
      }
      break;
      
    case EImageKitTransform.fill16x9:
      transformations.push('c-pad_resize');
      if (width) {
        const calculatedHeight = Math.round((width * 9) / 16);
        transformations.push(`h-${calculatedHeight}`);
      }
      transformations.push(`bg-${background}`);
      break;
      
    case EImageKitTransform.fill3x2:
      transformations.push('c-pad_resize');
      if (width) {
        const calculatedHeight = Math.round((width * 2) / 3);
        transformations.push(`h-${calculatedHeight}`);
      }
      transformations.push(`bg-${background}`);
      break;
      
    case EImageKitTransform.fill1x1:
      transformations.push('c-pad_resize');
      if (width) {
        transformations.push(`h-${width}`);
      }
      transformations.push(`bg-${background}`);
      break;
      
    case EImageKitTransform.fit16x9:
      transformations.push('c-at_max');
      if (width) {
        const calculatedHeight = Math.round((width * 9) / 16);
        transformations.push(`h-${calculatedHeight}`);
      }
      break;
      
    case EImageKitTransform.fit4x3:
      transformations.push('c-at_max');
      if (width) {
        const calculatedHeight = Math.round((width * 3) / 4);
        transformations.push(`h-${calculatedHeight}`);
      }
      break;
      
    case EImageKitTransform.fit3x2:
      transformations.push('c-at_max');
      if (width) {
        const calculatedHeight = Math.round((width * 2) / 3);
        transformations.push(`h-${calculatedHeight}`);
      }
      break;
      
    case EImageKitTransform.fit3x4:
      transformations.push('c-at_max');
      if (width) {
        const calculatedHeight = Math.round((width * 4) / 3);
        transformations.push(`h-${calculatedHeight}`);
      }
      break;
      
    case EImageKitTransform.fit1x1:
      transformations.push('c-at_max');
      if (width) {
        transformations.push(`h-${width}`);
      }
      break;
      
    case EImageKitTransform.limit:
      transformations.push('c-at_max');
      // Only apply width, no fixed height
      break;
      
    case EImageKitTransform.bgcrop:
      transformations.push('c-maintain_ratio');
      transformations.push('fo-center');
      if (width) {
        const calculatedHeight = Math.round(width / 2);
        transformations.push(`h-${calculatedHeight}`);
        transformations.push('cm-extract');
      }
      break;
      
    case EImageKitTransform.thumb3x4:
      transformations.push('c-maintain_ratio');
      transformations.push('fo-face'); // Focus on faces for thumbnails
      if (width) {
        const calculatedHeight = Math.round((width * 4) / 3);
        transformations.push(`h-${calculatedHeight}`);
        transformations.push('cm-extract');
      }
      break;
      
    case EImageKitTransform.thumb9x10:
      transformations.push('c-maintain_ratio');
      transformations.push('fo-face');
      if (width) {
        const calculatedHeight = Math.round((width * 10) / 9);
        transformations.push(`h-${calculatedHeight}`);
        transformations.push('cm-extract');
      }
      break;
      
    case EImageKitTransform.thumb1x1:
      transformations.push('c-maintain_ratio');
      transformations.push('fo-face');
      if (width) {
        transformations.push(`h-${width}`);
        transformations.push('cm-extract');
      }
      break;
  }

  // Handle custom height if provided (overrides preset height)
  if (height && !transformations.some(t => t.startsWith('h-'))) {
    transformations.push(`h-${height}`);
  }

  // Build the final URL
  // For web proxy pattern, we'll use the originURL as the path
  const transformationString = transformations.join(',');
  
  // Normalize the originURL to handle protocol-relative URLs
  let normalizedOriginURL = originURL;
  if (originURL.startsWith('//')) {
    // Convert protocol-relative URL to HTTPS
    normalizedOriginURL = `https:${originURL}`;
  }
  
  // Check if originURL is already a full URL or a path
  if (normalizedOriginURL.startsWith('http://') || normalizedOriginURL.startsWith('https://')) {
    // For external URLs, use web proxy pattern
    const encodedOriginURL = encodeURIComponent(normalizedOriginURL);
    return `${urlEndpoint}/tr:${transformationString}/${encodedOriginURL}`;
  } else {
    // For internal paths, append directly
    const cleanPath = normalizedOriginURL.startsWith('/') ? normalizedOriginURL.slice(1) : normalizedOriginURL;
    return `${urlEndpoint}/tr:${transformationString}/${cleanPath}`;
  }
};

// Helper function to convert WSRV preset to ImageKit preset
export const convertWsrvToImageKit = (wsrvPreset: EWsrvTransform): EImageKitTransform => {
  switch (wsrvPreset) {
    case EWsrvTransform.cover16x9:
      return EImageKitTransform.cover16x9;
    case EWsrvTransform.cover4x3:
      return EImageKitTransform.cover4x3;
    case EWsrvTransform.cover3x2:
      return EImageKitTransform.cover3x2;
    case EWsrvTransform.cover2x6:
      return EImageKitTransform.cover2x6;
    case EWsrvTransform.fill16x9:
      return EImageKitTransform.fill16x9;
    case EWsrvTransform.fill3x2:
      return EImageKitTransform.fill3x2;
    case EWsrvTransform.fill1x1:
      return EImageKitTransform.fill1x1;
    case EWsrvTransform.fit16x9:
      return EImageKitTransform.fit16x9;
    case EWsrvTransform.fit4x3:
      return EImageKitTransform.fit4x3;
    case EWsrvTransform.fit3x2:
      return EImageKitTransform.fit3x2;
    case EWsrvTransform.fit3x4:
      return EImageKitTransform.fit3x4;
    case EWsrvTransform.fit1x1:
      return EImageKitTransform.fit1x1;
    case EWsrvTransform.limit:
      return EImageKitTransform.limit;
    case EWsrvTransform.bgcrop:
      return EImageKitTransform.bgcrop;
    case EWsrvTransform.thumb3x4:
      return EImageKitTransform.thumb3x4;
    case EWsrvTransform.thumb9x10:
      return EImageKitTransform.thumb9x10;
    case EWsrvTransform.thumb1x1:
      return EImageKitTransform.thumb1x1;
    default:
      return EImageKitTransform.limit;
  }
};